import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Mail, Send, User, Phone, MessageSquare, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceInterests = [
  { value: 'welding', label: 'Welding Services' },
  { value: 'mechanics', label: 'Mechanic Services' },
  { value: 'handyman', label: 'Handyman Services' },
  { value: 'cleaning', label: 'Cleaning Services' },
  { value: 'multiple', label: 'Multiple Services' },
  { value: 'other', label: 'Other / General Inquiry' },
];

export default function Contact() {
  const [serviceAreas, setServiceAreas] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_interest: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const fetchServiceAreas = async () => {
      try {
        const response = await axios.get(`${API}/service-areas`);
        setServiceAreas(response.data);
      } catch (error) {
        console.error('Error fetching service areas:', error);
      }
    };
    fetchServiceAreas();
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.service_interest || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSuccess(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <main className="min-h-screen pt-24 pb-16 px-6 md:px-12 lg:px-24 flex items-center justify-center" data-testid="contact-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass rounded-2xl p-12 text-center max-w-lg"
        >
          <div className="w-20 h-20 rounded-full bg-[#00F0FF]/20 flex items-center justify-center mx-auto mb-6 cyan-glow">
            <Check className="w-10 h-10 text-[#00F0FF]" />
          </div>
          <h2 className="font-heading text-3xl font-bold mb-4">Message Sent!</h2>
          <p className="text-gray-400 mb-8">
            Thank you for reaching out. Our team will get back to you within 24 hours.
          </p>
          <Button
            onClick={() => {
              setIsSuccess(false);
              setFormData({
                name: '',
                email: '',
                phone: '',
                service_interest: '',
                message: '',
              });
            }}
            className="btn-primary rounded-none px-8 py-5 font-heading"
            data-testid="send-another-button"
          >
            SEND ANOTHER MESSAGE
          </Button>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-24 pb-16" data-testid="contact-page">
      {/* Hero Section */}
      <section className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#00F0FF] font-medium tracking-wider uppercase text-sm">
              Get In Touch
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black mt-4 mb-6">
              Contact <span className="gradient-text">Us</span>
            </h1>
            <p className="text-gray-400 max-w-2xl text-lg">
              Have questions? Need a quote? We're here to help. Send us a message 
              and we'll respond as soon as possible.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.form
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              onSubmit={handleSubmit}
              className="glass rounded-2xl p-8 md:p-10"
            >
              <h2 className="font-heading text-2xl font-bold mb-8">Send a Message</h2>
              
              <div className="space-y-6">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-300 flex items-center gap-2">
                    <User className="w-4 h-4 text-[#00F0FF]" />
                    Your Name *
                  </Label>
                  <Input
                    id="name"
                    data-testid="contact-name-input"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="bg-[#0A0F14] border-[#1E293B] text-white placeholder:text-gray-600 rounded-none focus:border-[#00F0FF]"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#00F0FF]" />
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-email-input"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="bg-[#0A0F14] border-[#1E293B] text-white placeholder:text-gray-600 rounded-none focus:border-[#00F0FF]"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-300 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[#00F0FF]" />
                    Phone Number (Optional)
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    data-testid="contact-phone-input"
                    placeholder="(443) 655-9580"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="bg-[#0A0F14] border-[#1E293B] text-white placeholder:text-gray-600 rounded-none focus:border-[#00F0FF]"
                  />
                </div>

                {/* Service Interest */}
                <div className="space-y-2">
                  <Label className="text-gray-300 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-[#00F0FF]" />
                    Service Interest *
                  </Label>
                  <Select
                    value={formData.service_interest}
                    onValueChange={(value) => handleChange('service_interest', value)}
                  >
                    <SelectTrigger 
                      data-testid="contact-service-select"
                      className="bg-[#0A0F14] border-[#1E293B] text-white rounded-none focus:border-[#00F0FF]"
                    >
                      <SelectValue placeholder="What service are you interested in?" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0F14] border-[#1E293B]">
                      {serviceInterests.map((interest) => (
                        <SelectItem 
                          key={interest.value} 
                          value={interest.value}
                          className="text-white hover:bg-[#1E293B] focus:bg-[#1E293B]"
                        >
                          {interest.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-gray-300 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-[#00F0FF]" />
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="contact-message-input"
                    placeholder="Tell us about your project or question..."
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    className="bg-[#0A0F14] border-[#1E293B] text-white placeholder:text-gray-600 rounded-none focus:border-[#00F0FF] min-h-[150px]"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={isSubmitting}
                  className="w-full btn-primary rounded-none py-6 font-heading tracking-wide text-lg"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2 justify-center">
                      <div className="spinner w-5 h-5" />
                      SENDING...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2 justify-center">
                      <Send className="w-5 h-5" />
                      SEND MESSAGE
                    </span>
                  )}
                </Button>
              </div>
            </motion.form>

            {/* Service Areas */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h2 className="font-heading text-2xl font-bold mb-8 flex items-center gap-3">
                <MapPin className="w-6 h-6 text-[#00F0FF]" />
                Service Areas
              </h2>
              
              <div className="space-y-4">
                {serviceAreas.map((area, index) => (
                  <motion.div
                    key={area.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className="area-card cursor-pointer group"
                    data-testid={`service-area-${area.id}`}
                  >
                    <h3 className="font-heading text-lg font-bold text-white group-hover:text-[#00F0FF] transition-colors mb-2">
                      {area.name}
                    </h3>
                    <p className="text-gray-400 text-sm mb-3">{area.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {area.zip_codes.map((zip) => (
                        <span
                          key={zip}
                          className="px-2 py-1 text-xs bg-[#1E293B] text-gray-400 rounded"
                        >
                          {zip}
                        </span>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Contact Info */}
              <div className="mt-12 glass rounded-xl p-6">
                <h3 className="font-heading text-lg font-bold mb-4">Direct Contact</h3>
                <div className="space-y-4 text-gray-400">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-[#00F0FF]" />
                    <span>(443) 655-9580</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-[#00F0FF]" />
                    <span>OmniBeastServices@gmail.com</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[#00F0FF] shrink-0 mt-0.5" />
                    <span>Bridgeport, WV<br />& Surrounding Areas</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
